/*
 * Sign-in: a two-step card. The address first (only its domain's brand is
 * asked for, which doesn't reveal which addresses exist), then the password.
 * The page shows the host's brand until then, and the mailbox's agency's after.
 */
import { useEffect, useRef, useState, type FormEvent } from "react";
import { Button } from "../ui/Button";
import { IconAlertCircle } from "../ui/icons";
import { ApiError, wmApi } from "./api";
import { IconMail } from "./icons";
import { useWebmailBrand } from "./brand";
import { BrandFooter, BrandMark } from "./parts";
import type { Account } from "./types";
import { Callout } from "../ui/Notice";

const EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export function SignIn({
  initialAddress,
  mode = "first",
  signedInAs,
  onSignedIn,
  onCancel,
}: {
  initialAddress: string | null;
  mode?: "first" | "add" | "again";
  signedInAs?: string;
  onSignedIn: (account: Account, accounts: Account[]) => void;
  onCancel?: () => void;
}) {
  const [address, setAddress] = useState(initialAddress ?? "");
  const [step, setStep] = useState<1 | 2>(initialAddress && EMAIL.test(initialAddress) ? 2 : 1);
  const [password, setPassword] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [fieldError, setFieldError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const passwordRef = useRef<HTMLInputElement>(null);
  const addressRef = useRef<HTMLInputElement>(null);
  useWebmailBrand(step === 2 ? address : null);

  useEffect(() => {
    document.title = mode === "add" ? "Add a mailbox · Webmail" : "Sign in · Webmail";
    (step === 2 ? passwordRef : addressRef).current?.focus();
  }, [step, mode]);

  const next = (e: FormEvent) => {
    e.preventDefault();
    const a = address.trim().toLowerCase();
    if (!EMAIL.test(a)) {
      setFieldError("Enter your full email address, like you@example.com.");
      return;
    }
    setAddress(a);
    setFieldError(null);
    setError(null);
    setStep(2);
  };

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (!password) {
      setFieldError("Enter your password.");
      return;
    }
    setBusy(true);
    setError(null);
    setFieldError(null);
    try {
      const r = await wmApi.post<{ account: Account; accounts: Account[] }>("/session", { address, password });
      setPassword("");
      onSignedIn(r.account, r.accounts);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Something went wrong. Try again.");
      setPassword("");
      passwordRef.current?.focus();
    } finally {
      setBusy(false);
    }
  };

  const heading = mode === "add" ? "Add a mailbox" : mode === "again" ? "Sign in again" : "Webmail";
  const intro =
    mode === "add"
      ? "You can switch mailboxes from the menu at the top right."
      : mode === "again"
        ? "The saved password stopped working. Enter it again."
        : "Sign in to read your email.";

  return (
    <div className="flex min-h-dvh flex-col bg-canvas">
      <header className="flex shrink-0 items-center border-b border-line bg-surface px-4 py-3">
        <BrandMark />
      </header>
      <main className="flex flex-1 items-center justify-center px-4 py-10">
        <div className="w-full max-w-sm">
          <div className="mb-6 text-center">
            <span aria-hidden="true" className="mx-auto mb-3 inline-flex h-11 w-11 items-center justify-center rounded-full bg-selected text-selected-fg">
              <IconMail size={22} />
            </span>
            <h1 className="text-[22px] font-semibold tracking-[-0.01em] text-fg">{heading}</h1>
            <p className="mt-1 text-[13.5px] text-dim">{intro}</p>
            {mode === "add" && signedInAs ? (
              <p className="mt-2 text-[13px] text-dim">
                You're signed in as <span className="font-medium text-fg">{signedInAs}</span>.
              </p>
            ) : null}
          </div>

          <div className="card p-5">
            {step === 1 ? (
              <form onSubmit={next} className="space-y-3" noValidate>
                <div>
                  <label htmlFor="wm-address" className="mb-1.5 block text-[13px] font-medium text-body">
                    Email address
                  </label>
                  <input
                    ref={addressRef}
                    id="wm-address"
                    type="email"
                    autoComplete="username"
                    className="field"
                    placeholder="you@example.com"
                    value={address}
                    aria-invalid={fieldError ? true : undefined}
                    aria-describedby={fieldError ? "wm-address-error" : undefined}
                    onChange={(e) => setAddress(e.target.value)}
                  />
                  {fieldError ? (
                    <p id="wm-address-error" className="mt-1 flex items-start gap-1.5 text-[12.5px] font-medium text-bad">
                      <IconAlertCircle size={14} className="mt-[2px] shrink-0" />
                      {fieldError}
                    </p>
                  ) : null}
                </div>
                <Button type="submit" variant="primary" className="w-full">
                  Continue
                </Button>
              </form>
            ) : (
              <form onSubmit={submit} className="space-y-3" noValidate>
                <div className="flex items-center justify-between gap-2 rounded-md bg-panel px-3 py-2 text-[13px]">
                  <span className="truncate text-body">
                    Signing in as <span className="font-medium text-fg">{address}</span>
                  </span>
                  <button
                    type="button"
                    className="shrink-0 text-link hover:underline"
                    onClick={() => {
                      setStep(1);
                      setError(null);
                      setPassword("");
                    }}
                  >
                    Change
                  </button>
                </div>
                {/* For password managers: the username belongs to this form too. */}
                <input type="email" autoComplete="username" value={address} readOnly hidden />
                <div>
                  <label htmlFor="wm-password" className="mb-1.5 block text-[13px] font-medium text-body">
                    Password
                  </label>
                  <input
                    ref={passwordRef}
                    id="wm-password"
                    type="password"
                    autoComplete="current-password"
                    className="field"
                    placeholder="Mailbox password"
                    value={password}
                    aria-invalid={fieldError ? true : undefined}
                    onChange={(e) => setPassword(e.target.value)}
                  />
                  {fieldError ? (
                    <p className="mt-1 flex items-start gap-1.5 text-[12.5px] font-medium text-bad">
                      <IconAlertCircle size={14} className="mt-[2px] shrink-0" />
                      {fieldError}
                    </p>
                  ) : null}
                </div>
                {error ? (
                  <Callout role="alert" tone="critical" title="Couldn't sign in">
                    {error}
                  </Callout>
                ) : null}
                <Button type="submit" variant="primary" className="w-full" loading={busy} loadingText="Signing in">
                  Sign in
                </Button>
              </form>
            )}
          </div>

          {onCancel ? (
            <p className="mt-4 text-center text-[11px] text-dim">
              <button type="button" className="text-link hover:underline" onClick={onCancel}>
                Back to {signedInAs ?? "your mail"}
              </button>
            </p>
          ) : (
            <p className="mt-4 text-center text-[11px] text-dim">Don't have a mailbox? Ask whoever manages your email.</p>
          )}
        </div>
      </main>
      <BrandFooter />
    </div>
  );
}
